'use client';

import React, { useState, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Send, CheckCircle2, Upload, X } from 'lucide-react';
import { trackEvent, trackGTMEvent, AnalyticsEvents } from '@/lib/analytics';
import { resolveSource } from '@/lib/source-tracking';
import { getBrandColor } from '@/config/brand-colors';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { PhoneInput, COUNTRIES, Country } from '@/components/ui/phone-input';

interface FormField {
  id: string;
  name: string;
  label: string;
  type: string;
  placeholder?: string | null;
  required: boolean;
  options?: unknown;
  validation?: unknown;
  order: number;
}

interface Form {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  fields: FormField[];
  settings?: Record<string, any>;
}

interface FormRendererProps {
  form: Form;
  onSubmit?: (data: Record<string, any>) => Promise<void>;
  submitUrl?: string;
  className?: string;
  darkMode?: boolean;
}

interface UploadedFileSubmission {
  fileName: string;
  fileSize: number;
  fileType: string;
  s3Key: string;
  fileUrl?: string;
  uploadedAt: string;
}

interface UploadedFileCacheEntry {
  file: File;
  submissionValue: UploadedFileSubmission;
}

const getUploadErrorMessage = (fieldLabel: string, detail?: string): string => {
  const normalizedDetail = detail?.trim().replace(/[.!?]+$/, '');

  if (normalizedDetail && normalizedDetail !== 'Failed to upload file') {
    return `Unable to upload ${fieldLabel}: ${normalizedDetail}. Please try again.`;
  }

  return `Unable to upload ${fieldLabel}. Please try again.`;
};

export default function FormRenderer({
  form,
  onSubmit,
  submitUrl,
  className = '',
  darkMode = false,
}: FormRendererProps) {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const uploadedFilesRef = useRef<Record<string, UploadedFileCacheEntry>>({});

  // Get button text from settings or use form-specific defaults
  const getButtonText = (): string => {
    const settings = form.settings as Record<string, any> | undefined;
    
    // Check if custom button text is set in settings
    if (settings?.buttonText) {
      return settings.buttonText as string;
    }
    
    // Form-specific defaults based on slug
    const slug = form.slug?.toLowerCase() || '';
    if (slug === 'subscription') {
      return 'Subscribe';
    }
    if (slug === 'join-us' || slug === 'joinus') {
      return 'Submit Application';
    }
    if (slug === 'contact') {
      return 'Send Message';
    }
    
    // Default fallback
    return 'Submit';
  };

  const getSubmittingText = (): string => {
    const settings = form.settings as Record<string, any> | undefined;
    
    // Check if custom submitting text is set in settings
    if (settings?.submittingText) {
      return settings.submittingText as string;
    }
    
    // Form-specific defaults
    const slug = form.slug?.toLowerCase() || '';
    if (slug === 'subscription') {
      return 'Subscribing...';
    }
    if (slug === 'join-us' || slug === 'joinus') {
      return 'Submitting...';
    }
    
    // Default fallback
    return 'Sending...';
  };

  const getResubmitText = (): string => {
    const settings = form.settings as Record<string, any> | undefined;
    
    // Check if custom resubmit text is set in settings
    if (settings?.resubmitText) {
      return settings.resubmitText as string;
    }
    
    // Form-specific defaults
    const slug = form.slug?.toLowerCase() || '';
    if (slug === 'subscription') {
      return 'Subscribe Again';
    }
    if (slug === 'join-us' || slug === 'joinus') {
      return 'Submit Another Application';
    }
    if (slug === 'contact') {
      return 'Send Another Message';
    }
    
    // Default fallback
    return 'Submit Again';
  };

  const getSuccessTitle = (): string => {
    const settings = form.settings as Record<string, any> | undefined;
    
    // Check if custom success title is set in settings
    if (settings?.successTitle) {
      return settings.successTitle as string;
    }
    
    // Form-specific defaults
    const slug = form.slug?.toLowerCase() || '';
    if (slug === 'subscription') {
      return 'Subscribed!';
    }
    if (slug === 'join-us' || slug === 'joinus') {
      return 'Application Submitted!';
    }
    if (slug === 'contact') {
      return 'Message Sent!';
    }
    
    // Default fallback
    return 'Success!';
  };

  const getSuccessMessage = (): string => {
    const settings = form.settings as Record<string, any> | undefined;
    
    // Check if custom success message is set in settings
    if (settings?.successMessage) {
      return settings.successMessage as string;
    }
    
    // Form-specific defaults
    const slug = form.slug?.toLowerCase() || '';
    if (slug === 'subscription') {
      return "Thank you for subscribing! You'll receive our latest updates and news.";
    }
    if (slug === 'join-us' || slug === 'joinus') {
      return "Thank you for your interest! We'll review your application and get back to you soon.";
    }
    if (slug === 'contact') {
      return "Thank you for reaching out. We'll get back to you within 24 hours.";
    }
    
    // Default fallback
    return 'Your submission was successful. Thank you!';
  };

  const handleChange = (name: string, value: any) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleFileChange = (name: string, file: File | null) => {
    delete uploadedFilesRef.current[name];
    setFiles((prev) => ({ ...prev, [name]: file }));
    // Store file name in formData for display
    setFormData((prev) => ({ ...prev, [name]: file ? file.name : '' }));
    // Clear error when file is selected
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const removeFile = (name: string) => {
    delete uploadedFilesRef.current[name];
    setFiles((prev) => ({ ...prev, [name]: null }));
    setFormData((prev) => ({ ...prev, [name]: '' }));
    setErrors((prev) => {
      if (!prev[name]) {
        return prev;
      }

      const nextErrors = { ...prev };
      delete nextErrors[name];
      return nextErrors;
    });
    // Reset file input
    if (fileInputRefs.current[name]) {
      fileInputRefs.current[name]!.value = '';
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    form.fields.forEach((field) => {
      const validation = field.validation as
        | {
            accept?: string;
            maxSize?: number;
            pattern?: string;
            requiredMessage?: string;
            invalidMessage?: string;
            fileTypeMessage?: string;
            fileSizeMessage?: string;
          }
        | undefined;

      if (field.required && !formData[field.name]) {
        newErrors[field.name] =
          validation?.requiredMessage ?? `${field.label} is required`;
      }

      // Email validation
      if (field.type === 'EMAIL' && formData[field.name]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData[field.name])) {
          newErrors[field.name] =
            validation?.invalidMessage ?? 'Please enter a valid email address';
        }
      }

      // URL validation
      if (field.type === 'URL' && formData[field.name]) {
        try {
          new URL(formData[field.name]);
        } catch {
          newErrors[field.name] =
            validation?.invalidMessage ?? 'Please enter a valid URL';
        }
      }

      // FILE validation
      if (field.type === 'FILE' && field.required && !files[field.name]) {
        newErrors[field.name] =
          validation?.requiredMessage ?? `${field.label} is required`;
      }

      // File type and size validation
      if (field.type === 'FILE' && files[field.name]) {
        const file = files[field.name];
        if (!file) return; // Safety check

        // Check file type
        if (validation?.accept) {
          const acceptedTypes = validation.accept.split(',').map((t) => t.trim());
          const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
          const mimeType = file.type;

          const isAccepted = acceptedTypes.some((accept) => {
            if (accept.startsWith('.')) {
              return accept === fileExtension;
            }
            if (accept.includes('/*')) {
              const baseType = accept.split('/')[0];
              return mimeType.startsWith(baseType + '/');
            }
            return accept === mimeType;
          });

          if (!isAccepted) {
            newErrors[field.name] =
              validation?.fileTypeMessage ??
              `File type not allowed. Accepted types: ${validation.accept}`;
          }
        }

        // Check file size - default to 4.5 MB if not specified
        const defaultMaxSize = 4.5 * 1024 * 1024; // 4.5 MB in bytes
        const maxSize = validation?.maxSize ?? defaultMaxSize;

        if (file.size > maxSize) {
          const maxSizeMB = maxSize / (1024 * 1024);
          newErrors[field.name] =
            validation?.fileSizeMessage ?? `File size exceeds ${maxSizeMB}MB limit`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare submission data with file information
      const submissionData: Record<string, any> = { ...formData };

      // Attach marketing source (`from` URL param, or 'direct') for attribution
      const source = resolveSource();
      submissionData.from = source;

      // Handle file uploads if any - upload to S3 first
      const fileFields = form.fields.filter((f) => f.type === 'FILE');
      const uploadErrors: Record<string, string> = {};

      for (const field of fileFields) {
        const file = files[field.name];
        if (file) {
          const cachedUpload = uploadedFilesRef.current[field.name];
          if (cachedUpload?.file === file) {
            submissionData[field.name] = cachedUpload.submissionValue;
            continue;
          }

          try {
            // Upload file to S3
            const fileFormData = new FormData();
            fileFormData.append('file', file);
            // Note: folder is set to 'application' in the upload endpoint
            fileFormData.append('title', `${form.slug}_${field.name}_${Date.now()}`);

            // Use public form upload endpoint
            const uploadResponse = await fetch(`/api/forms/${form.slug}/upload`, {
              method: 'POST',
              body: fileFormData,
            });

            if (uploadResponse.ok) {
              const uploadData = await uploadResponse.json();
              // Response structure: { success: true, data: { s3Key, fileUrl, ... } }
              const fileData = uploadData.data || uploadData;

              if (
                typeof fileData.s3Key !== 'string' ||
                fileData.s3Key.trim().length === 0
              ) {
                console.error('File upload response is missing an S3 key:', {
                  formSlug: form.slug,
                  fieldName: field.name,
                });
                uploadErrors[field.name] = getUploadErrorMessage(field.label);
                continue;
              }

              // Store S3 key and file metadata in submission only after a
              // complete upload response has been verified.
              const uploadedFile: UploadedFileSubmission = {
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type,
                s3Key: fileData.s3Key,
                fileUrl: fileData.fileUrl,
                uploadedAt: new Date().toISOString(),
              };
              uploadedFilesRef.current[field.name] = {
                file,
                submissionValue: uploadedFile,
              };
              submissionData[field.name] = uploadedFile;
            } else {
              // If upload fails, get error message from response
              const errorText = await uploadResponse.text();
              let errorMessage = 'Failed to upload file';
              try {
                const errorData = JSON.parse(errorText);
                errorMessage = errorData.error || errorMessage;
              } catch {
                errorMessage = errorText || errorMessage;
              }
              console.error('File upload failed:', {
                status: uploadResponse.status,
                statusText: uploadResponse.statusText,
                error: errorMessage,
                formSlug: form.slug,
                fieldName: field.name,
              });
              uploadErrors[field.name] = getUploadErrorMessage(
                field.label,
                errorMessage
              );
            }
          } catch (uploadError: unknown) {
            console.error('File upload error:', {
              error: uploadError,
              message:
                uploadError instanceof Error ? uploadError.message : undefined,
              formSlug: form.slug,
              fieldName: field.name,
            });
            const errorMessage =
              uploadError instanceof Error
                ? uploadError.message
                : 'Failed to upload file';
            uploadErrors[field.name] = getUploadErrorMessage(
              field.label,
              errorMessage
            );
          }
        }
      }

      // Uploads are part of the submission transaction. Keep the selected
      // files in state, show each field-level failure, and let the user retry.
      if (Object.keys(uploadErrors).length > 0) {
        setErrors((previousErrors) => ({
          ...previousErrors,
          ...uploadErrors,
        }));
        return;
      }

      if (onSubmit) {
        await onSubmit(submissionData);
      } else if (submitUrl) {
        const response = await fetch(submitUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ data: submissionData }),
        });

        if (!response.ok) {
          throw new Error('Failed to submit form');
        }
      } else {
        // Default: submit to /api/forms/[slug]/submit
        const response = await fetch(`/api/forms/${form.slug}/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ data: submissionData }),
        });

        if (!response.ok) {
          throw new Error('Failed to submit form');
        }
      }

      // Track Lead event
      trackEvent({
        name: AnalyticsEvents.LEAD,
        properties: {
          form_name: form.name,
          form_slug: form.slug,
          from: source,
        }
      });

      // GTM conversion event, fired only once the server confirms the
      // submission — mirrors the join_us_form setup on jobs.e-voque.com.
      trackGTMEvent('lead_form_success', {
        form_name: form.slug,
        from: source,
      });

      // Meta's standard event for job applications. Fires alongside Lead so
      // the Events Manager can attribute to the SubmitApplication funnel.
      if (form.slug === 'join-us') {
        trackEvent({
          name: AnalyticsEvents.SUBMIT_APPLICATION,
          properties: {
            form_name: form.name,
            form_slug: form.slug,
            from: source,
          }
        });
      }

      setIsSubmitted(true);
    } catch (error: any) {
      console.error('Error submitting form:', error);
      alert('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field: FormField) => {
    const fieldId = `field-${field.id}`;
    const hasError = !!errors[field.name];

    switch (field.type) {
      case 'PASSWORD':
        return (
          <div key={field.id} className="space-y-2">
            <label
              htmlFor={fieldId}
              className="text-sm font-medium"
              style={{ color: darkMode ? '#ffffff' : getBrandColor('deepNavy') }}
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <PasswordInput
              id={fieldId}
              name={field.name}
              placeholder={field.placeholder || ''}
              required={field.required}
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              className={`h-10 ${darkMode ? 'text-white placeholder:text-zinc-400' : ''}`}
              style={{
                borderColor: hasError
                  ? '#ef4444'
                  : darkMode
                  ? '#3f3f46'
                  : `${getBrandColor('deepNavy')}40`,
                backgroundColor: darkMode ? '#27272a' : getBrandColor('white'),
                color: darkMode ? '#ffffff' : undefined,
              }}
            />
            {hasError && (
              <p className="text-sm text-red-500">{errors[field.name]}</p>
            )}
          </div>
        );
      case 'PHONE':
        // Get countries from field options/validation
        // Empty array or null/undefined means ALL countries are available
        const getPhoneCountries = (): Country[] => {
          const fieldOptions = field.options as { countries?: string[] } | null;
          const fieldValidation = field.validation as { countries?: string[] } | null;
          const allowedCountries = fieldOptions?.countries || fieldValidation?.countries;
          
          // If no countries specified or empty array, use ALL countries
          if (!allowedCountries || allowedCountries.length === 0) {
            return COUNTRIES;
          }
          
          return COUNTRIES.filter(country => allowedCountries.includes(country.code));
        };

        const phoneCountries = getPhoneCountries();
        const fieldOptions = field.options as { defaultCountry?: string; defaultCountries?: string[] } | null;
        const defaultCountryCode = fieldOptions?.defaultCountry || 'CO';
        const defaultCountry = phoneCountries.find(c => c.code === defaultCountryCode)?.code || phoneCountries[0]?.code || 'CO';
        const defaultCountries = fieldOptions?.defaultCountries || [];

        return (
          <div key={field.id} className="space-y-2">
            <label
              htmlFor={fieldId}
              className="text-sm font-medium"
              style={{ color: darkMode ? '#ffffff' : getBrandColor('deepNavy') }}
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <PhoneInput
              id={fieldId}
              name={field.name}
              value={formData[field.name] || ''}
              onChange={(value) => handleChange(field.name, value)}
              defaultCountry={defaultCountry}
              defaultCountries={defaultCountries}
              countries={phoneCountries}
              placeholder={field.placeholder || ''}
              required={field.required}
              className={darkMode ? 'text-white placeholder:text-zinc-400' : ''}
            />
            {hasError && (
              <p className="text-sm text-red-500">{errors[field.name]}</p>
            )}
          </div>
        );

      case 'TEXT':
      case 'EMAIL':
      case 'URL':
        return (
          <div key={field.id} className="space-y-2">
            <label
              htmlFor={fieldId}
              className="text-sm font-medium"
              style={{ color: darkMode ? '#ffffff' : getBrandColor('deepNavy') }}
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Input
              id={fieldId}
              name={field.name}
              type={
                field.type === 'EMAIL'
                  ? 'email'
                  : field.type === 'URL'
                  ? 'url'
                  : 'text'
              }
              placeholder={field.placeholder || ''}
              required={field.required}
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              className={`h-10 ${darkMode ? 'text-white placeholder:text-zinc-400' : ''}`}
              style={{
                borderColor: hasError
                  ? '#ef4444'
                  : darkMode
                  ? '#3f3f46'
                  : `${getBrandColor('deepNavy')}40`,
                backgroundColor: darkMode ? '#27272a' : getBrandColor('white'),
                color: darkMode ? '#ffffff' : undefined,
              }}
            />
            {hasError && (
              <p className="text-sm text-red-500">{errors[field.name]}</p>
            )}
          </div>
        );

      case 'TEXTAREA':
        return (
          <div key={field.id} className="space-y-2">
            <label
              htmlFor={fieldId}
              className="text-sm font-medium"
              style={{ color: darkMode ? '#ffffff' : getBrandColor('deepNavy') }}
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Textarea
              id={fieldId}
              name={field.name}
              placeholder={field.placeholder || ''}
              required={field.required}
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              className={`min-h-24 resize-none ${darkMode ? 'text-white placeholder:text-zinc-400' : ''}`}
              style={{
                borderColor: hasError
                  ? '#ef4444'
                  : darkMode
                  ? '#3f3f46'
                  : `${getBrandColor('deepNavy')}40`,
                backgroundColor: darkMode ? '#27272a' : getBrandColor('white'),
                color: darkMode ? '#ffffff' : undefined,
              }}
            />
            {hasError && (
              <p className="text-sm text-red-500">{errors[field.name]}</p>
            )}
          </div>
        );

      case 'SELECT': {
        const selectOptions =
          field.options && Array.isArray(field.options)
            ? field.options
            : field.options && typeof field.options === 'object' && field.options !== null
            ? Object.entries(field.options).map(([key, value]) => ({
                label: String(value),
                value: key,
              }))
            : [];

        return (
          <div key={field.id} className="space-y-2">
            <label
              htmlFor={fieldId}
              className="text-sm font-medium"
              style={{ color: darkMode ? '#ffffff' : getBrandColor('deepNavy') }}
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Select
              value={formData[field.name] || ''}
              onValueChange={(value) => handleChange(field.name, value)}
              required={field.required}
            >
              <SelectTrigger
                id={fieldId}
                className={`h-10 ${darkMode ? 'text-white' : ''}`}
                style={{
                  borderColor: hasError
                    ? '#ef4444'
                    : darkMode
                    ? '#3f3f46'
                    : `${getBrandColor('deepNavy')}40`,
                  backgroundColor: darkMode ? '#27272a' : getBrandColor('white'),
                  color: darkMode ? '#ffffff' : undefined,
                }}
              >
                <SelectValue placeholder={field.placeholder || 'Select...'} />
              </SelectTrigger>
              <SelectContent className={darkMode ? 'bg-zinc-800 border-zinc-700' : ''}>
                {selectOptions.map((option: { value?: string; label?: string } | string, index: number) => {
                  const optionValue = typeof option === 'string' ? option : option.value || String(index);
                  const optionLabel = typeof option === 'string' ? option : option.label || optionValue;
                  return (
                    <SelectItem 
                      key={optionValue} 
                      value={optionValue}
                      className={darkMode ? 'text-white hover:bg-zinc-700' : ''}
                    >
                      {optionLabel}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {hasError && (
              <p className="text-sm text-red-500">{errors[field.name]}</p>
            )}
          </div>
        );
      }

      case 'RADIO': {
        const radioOptions =
          field.options && Array.isArray(field.options)
            ? field.options
            : field.options && typeof field.options === 'object' && field.options !== null
            ? Object.entries(field.options).map(([key, value]) => ({
                label: String(value),
                value: key,
              }))
            : [];

        return (
          <div key={field.id} className="space-y-2">
            <label
              className="text-sm font-medium"
              style={{ color: darkMode ? '#ffffff' : getBrandColor('deepNavy') }}
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <RadioGroup
              value={formData[field.name] || ''}
              onValueChange={(value) => handleChange(field.name, value)}
              required={field.required}
            >
              {radioOptions.map((option: { value?: string; label?: string } | string, index: number) => {
                const optionValue = typeof option === 'string' ? option : option.value || String(index);
                const optionLabel = typeof option === 'string' ? option : option.label || optionValue;
                return (
                  <div key={optionValue} className="flex items-center space-x-2">
                    <RadioGroupItem
                      value={optionValue}
                      id={`${fieldId}-${optionValue}`}
                    />
                    <Label
                      htmlFor={`${fieldId}-${optionValue}`}
                      className="font-normal"
                      style={{ color: darkMode ? '#ffffff' : getBrandColor('deepNavy') }}
                    >
                      {optionLabel}
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
            {hasError && (
              <p className="text-sm text-red-500">{errors[field.name]}</p>
            )}
          </div>
        );
      }

      case 'CHECKBOX':
        return (
          <div key={field.id} className="flex items-center space-x-2">
            <Checkbox
              id={fieldId}
              checked={!!formData[field.name]}
              onCheckedChange={(checked) =>
                handleChange(field.name, checked)
              }
              required={field.required}
            />
            <label
              htmlFor={fieldId}
              className="text-sm font-medium"
              style={{ color: darkMode ? '#ffffff' : getBrandColor('deepNavy') }}
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {hasError && (
              <p className="text-sm text-red-500 ml-2">{errors[field.name]}</p>
            )}
          </div>
        );

      case 'NUMBER':
        return (
          <div key={field.id} className="space-y-2">
            <label
              htmlFor={fieldId}
              className="text-sm font-medium"
              style={{ color: darkMode ? '#ffffff' : getBrandColor('deepNavy') }}
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Input
              id={fieldId}
              name={field.name}
              type="number"
              placeholder={field.placeholder || ''}
              required={field.required}
              value={formData[field.name] || ''}
              onChange={(e) =>
                handleChange(field.name, Number(e.target.value))
              }
              className={`h-10 ${darkMode ? 'text-white placeholder:text-zinc-400' : ''}`}
              style={{
                borderColor: hasError
                  ? '#ef4444'
                  : darkMode
                  ? '#3f3f46'
                  : `${getBrandColor('deepNavy')}40`,
                backgroundColor: darkMode ? '#27272a' : getBrandColor('white'),
                color: darkMode ? '#ffffff' : undefined,
              }}
            />
            {hasError && (
              <p className="text-sm text-red-500">{errors[field.name]}</p>
            )}
          </div>
        );

      case 'DATE':
        return (
          <div key={field.id} className="space-y-2">
            <label
              htmlFor={fieldId}
              className="text-sm font-medium"
              style={{ color: darkMode ? '#ffffff' : getBrandColor('deepNavy') }}
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Input
              id={fieldId}
              name={field.name}
              type="date"
              required={field.required}
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              className={`h-10 ${darkMode ? 'text-white' : ''}`}
              style={{
                borderColor: hasError
                  ? '#ef4444'
                  : darkMode
                  ? '#3f3f46'
                  : `${getBrandColor('deepNavy')}40`,
                backgroundColor: darkMode ? '#27272a' : getBrandColor('white'),
                color: darkMode ? '#ffffff' : undefined,
              }}
            />
            {hasError && (
              <p className="text-sm text-red-500">{errors[field.name]}</p>
            )}
          </div>
        );

      case 'TIME':
        return (
          <div key={field.id} className="space-y-2">
            <label
              htmlFor={fieldId}
              className="text-sm font-medium"
              style={{ color: darkMode ? '#ffffff' : getBrandColor('deepNavy') }}
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Input
              id={fieldId}
              name={field.name}
              type="time"
              required={field.required}
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              className={`h-10 ${darkMode ? 'text-white' : ''}`}
              style={{
                borderColor: hasError
                  ? '#ef4444'
                  : darkMode
                  ? '#3f3f46'
                  : `${getBrandColor('deepNavy')}40`,
                backgroundColor: darkMode ? '#27272a' : getBrandColor('white'),
                color: darkMode ? '#ffffff' : undefined,
              }}
            />
            {hasError && (
              <p className="text-sm text-red-500">{errors[field.name]}</p>
            )}
          </div>
        );

      case 'FILE': {
        const file = files[field.name];
        const validation = field.validation as {
          accept?: string;
          maxSize?: number;
        } | undefined;
        const acceptTypes = validation?.accept || '*/*';
        // Default to 4.5 MB if not specified
        const defaultMaxSize = 4.5 * 1024 * 1024; // 4.5 MB in bytes
        const maxSize = validation?.maxSize ?? defaultMaxSize;
        const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
        // Button label is configurable per-field (e.g. "Attach your resume")
        const fileButtonText =
          (field.options as { buttonText?: string } | null)?.buttonText || 'Choose File';
        // Public-facing accepted-types label can differ from the real `accept`
        // (e.g. hide `image/*` from the page while still allowing images).
        const acceptLabel =
          (field.options as { acceptLabel?: string } | null)?.acceptLabel || acceptTypes;

        return (
          <div key={field.id} className="space-y-2">
            <label
              htmlFor={fieldId}
              className="text-sm font-medium"
              style={{ color: darkMode ? '#ffffff' : getBrandColor('deepNavy') }}
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {file ? (
              <div className="flex items-center gap-2 p-3 rounded-lg border" style={{
                borderColor: hasError
                  ? '#ef4444'
                  : darkMode
                  ? '#3f3f46'
                  : `${getBrandColor('deepNavy')}40`,
                backgroundColor: darkMode ? '#27272a' : getBrandColor('white'),
              }}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: darkMode ? '#ffffff' : getBrandColor('deepNavy') }}>
                    {file.name}
                  </p>
                  <p className={`text-xs ${darkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFile(field.name)}
                  aria-label={`Remove ${file.name}`}
                  className={`h-8 w-8 p-0 ${darkMode ? 'text-white hover:bg-zinc-700' : ''}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div>
                <input
                  ref={(el) => {
                    fileInputRefs.current[field.name] = el;
                  }}
                  id={fieldId}
                  name={field.name}
                  type="file"
                  accept={acceptTypes}
                  required={field.required}
                  onChange={(e) => {
                    const selectedFile = e.target.files?.[0] || null;
                    handleFileChange(field.name, selectedFile);
                  }}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRefs.current[field.name]?.click()}
                  className={`w-full h-10 ${darkMode ? 'text-white border-zinc-700 bg-zinc-800 hover:bg-zinc-700' : ''}`}
                  style={{
                    borderColor: hasError
                      ? '#ef4444'
                      : darkMode
                      ? '#3f3f46'
                      : `${getBrandColor('deepNavy')}40`,
                    backgroundColor: darkMode ? '#27272a' : getBrandColor('white'),
                  }}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {fileButtonText}
                </Button>
              </div>
            )}
            {field.placeholder && !file && (
              <p className={`text-xs ${darkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{field.placeholder}</p>
            )}
            <p className={`text-xs ${darkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
              Maximum file size: {maxSizeMB} MB
            </p>
            {acceptLabel !== '*/*' && (
              <p className={`text-xs ${darkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                Accepted types: {acceptLabel}
              </p>
            )}
            {hasError && (
              <p className="text-sm text-red-500">{errors[field.name]}</p>
            )}
          </div>
        );
      }

      default:
        return (
          <div key={field.id} className="space-y-2">
            <label
              htmlFor={fieldId}
              className="text-sm font-medium"
              style={{ color: darkMode ? '#ffffff' : getBrandColor('deepNavy') }}
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Input
              id={fieldId}
              name={field.name}
              type="text"
              placeholder={field.placeholder || ''}
              required={field.required}
              value={formData[field.name] || ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              className={`h-10 ${darkMode ? 'text-white placeholder:text-zinc-400' : ''}`}
              style={{
                borderColor: hasError
                  ? '#ef4444'
                  : darkMode
                  ? '#3f3f46'
                  : `${getBrandColor('deepNavy')}40`,
                backgroundColor: darkMode ? '#27272a' : getBrandColor('white'),
                color: darkMode ? '#ffffff' : undefined,
              }}
            />
            {hasError && (
              <p className="text-sm text-red-500">{errors[field.name]}</p>
            )}
          </div>
        );
    }
  };

  if (isSubmitted) {
    return (
      <div className="flex h-full flex-col items-center justify-center py-12 px-4 md:px-0 text-center">
        <div
          className="flex h-20 w-20 items-center justify-center rounded-full"
          style={{ backgroundColor: `${getBrandColor('greenAccent')}20` }}
        >
          <CheckCircle2
            className="h-10 w-10"
            style={{ color: getBrandColor('greenAccent') }}
          />
        </div>
        <h3
          className="mt-6 text-2xl font-bold"
          style={{ color: darkMode ? '#ffffff' : getBrandColor('deepNavy') }}
        >
          {getSuccessTitle()}
        </h3>
        <p className="mt-3 max-w-sm" style={{ color: darkMode ? '#a1a1aa' : '#4a4a4a' }}>
          {getSuccessMessage()}
        </p>
        <Button
          onClick={() => {
            setIsSubmitted(false);
            setFormData({});
            setFiles({});
            setErrors({});
            uploadedFilesRef.current = {};
            // Reset all file inputs
            Object.keys(fileInputRefs.current).forEach((key) => {
              if (fileInputRefs.current[key]) {
                fileInputRefs.current[key]!.value = '';
              }
            });
          }}
          variant="outline"
          className="mt-8"
          style={{
            borderColor: getBrandColor('deepNavy'),
            color: getBrandColor('deepNavy'),
          }}
        >
          {getResubmitText()}
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-3 px-4 md:px-0 ${className}`}>
      {form.fields.map((field, index) => {
        // Check if this is firstName and next field is lastName
        if (field.name === 'firstName') {
          const nextField = form.fields[index + 1];
          if (nextField?.name === 'lastName') {
            // Render both fields in a two-column grid
            return (
              <div key={`${field.id}-${nextField.id}`} className="grid gap-3 sm:grid-cols-2">
                {renderField(field)}
                {renderField(nextField)}
              </div>
            );
          }
        }
        
        // Skip lastName if it was already rendered with firstName
        if (field.name === 'lastName' && form.fields[index - 1]?.name === 'firstName') {
          return null;
        }
        
        // Render single field
        return <React.Fragment key={field.id}>{renderField(field)}</React.Fragment>;
      })}

      <Button
        type="submit"
        className="h-10 w-full transition-all duration-300"
        disabled={isSubmitting}
        size="lg"
        style={{
          backgroundColor: '#4A8A3A', // Darker green for better contrast (WCAG AA compliant: 4.5:1 with white)
          color: getBrandColor('white'),
        }}
      >
        {isSubmitting ? (
          <>
            <svg
              className="mr-2 h-4 w-4 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            {getSubmittingText()}
          </>
        ) : (
          <>
            {getButtonText()}
            <Send className="ml-2 h-4 w-4" />
          </>
        )}
      </Button>
    </form>
  );
}
